import { css } from '@emotion/react';
import { useContext } from 'react';
import { AngularContext } from 'react-app';
import t from 'react-translate';
import { MentoringProgramGuide } from 'redux/schemas/models/mentoring-program-guides';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import prodPathReplace from 'shared/prod-path-rewrite';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';
import { black, gray5, gray6, hexToRgbaString } from 'styles/global_defaults/colors';
import { novoEdBlue } from 'athena/styles/colors';
import { config } from '@config/pendo.config.json';


type GuidanceItemProps = {
  guide: MentoringProgramGuide;
};

const styles = (guide: MentoringProgramGuide) => css`
  max-width: 400px;
  height: 50px;

  border-radius: ${quarterSpacing}px;
  border: 1px solid ${gray6};
  overflow: hidden;

  &:hover {
    border: 1px solid ${gray5};
    color: ${novoEdBlue};
  }

  .tile {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;

    background-image: url(${prodPathReplace('images/guide-tile-bg.svg')});
    ${guide.video?.thumbnailUrl && css`
      background-image: url("${guide.video.thumbnailUrl}");
    `}
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
  }

  ${guide.video?.thumbnailUrl && css`
    .overlay {
      background-color: ${hexToRgbaString(black, 0.1)};
    }
  `}
`;

const GuidanceItem = ({ guide }: GuidanceItemProps) => {
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);

  const setGuideIdParam = () => {
    const params = { guideId: guide.id };
    $state.go($state.current, params, { notify: false });
  };

  return (
    <ClickableContainer
      className='d-flex align-items-center'
      onClick={(e) => {
        e.stopPropagation();
        setGuideIdParam();
      }}
      css={styles(guide)}
      data-qa={config.pendo.athena.mentorshipProgram.participantHome.guidance.section.item.showGuide}
      data-qa-id={`${config.pendo.athena.mentorshipProgram.participantHome.guidance.section.item.showGuide}-${guide.id}`}
      aria-label={t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GUIDANCE_SECTION.ARIA_LABEL.SHOW_GUIDE(guide.title)}
    >
      <div className='tile'>
        <div className='overlay'>
          <NvIcon
            className={`p-3 ${guide.video ? 'color-white' : 'color-yellow'}`}
            icon={guide.video ? 'play' : 'read'}
            size='small'
          />
        </div>
      </div>
      <div className='px-2 semi-bold two-line-ellipsis'>{guide.title}</div>
    </ClickableContainer>
  );
};

export default GuidanceItem;
